import type { ARCPattern, ARCPatternResult, LiveSnapshot } from "@/arc/arc.types";

export const qaFailuresPattern: ARCPattern = {
  id: "qa_failures",
  label: "QA Failures & Defects",
  owner: "qa",
  urgency: "high",

  evaluate(snapshot: LiveSnapshot): ARCPatternResult {
    const { jobs, issues } = snapshot;

    const qaIssues = issues.filter(
      (i) =>
        (i.status === "Open" || i.status === "In Progress") &&
        (i.source === "QA Inspector" || i.issueType === "QA Issue" || i.jobStageAtCreation === "QA Review")
    );

    const qaJobIds = new Set(qaIssues.map((i) => i.jobId));

    const qaJobs = jobs.filter(
      (j) =>
        j.currentStage === "QA Review" ||
        qaJobIds.has(j.id)
    );

    const jobRev = (j: typeof jobs[0]) =>
      j.financials?.contractValue ?? j.payoutAmount ?? 0;

    const amountAtRisk = qaJobs.reduce((s, j) => s + jobRev(j), 0);

    const summary = qaJobs.map((j) => {
      const relatedIssues = qaIssues.filter((i) => i.jobId === j.id);
      const issueLabel = relatedIssues.length > 0
        ? relatedIssues.map((i) => i.issueType).join(", ")
        : "QA Review pending";
      return `${j.customerName} — ${issueLabel}`;
    });

    return {
      triggered: qaJobs.length > 0,
      count: qaJobs.length,
      affectedJobIds: qaJobs.map((j) => j.id),
      amountAtRisk,
      context:
        qaJobs.length > 0
          ? `${qaJobs.length} job${qaJobs.length > 1 ? "s" : ""} with QA failures or active defects — $${amountAtRisk.toLocaleString()} in final payments blocked`
          : "No active QA defects",
      suggestedAction: "Assign repair crews to open defects — final payment is held until QA passes",
      snapshot: {
        qaJobIds: qaJobs.map((j) => j.id),
        openQAIssueCount: qaIssues.length,
        summary,
        amountAtRisk,
      },
    };
  },
};
